/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import React from 'react';
import { toast } from 'react-toastify';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import { usePdfContext } from '@/components/Providers/PdfProvider';
import Button from '@/components/V2/Button';
import AuthToast from '@/components/V2/Popup/AuthToast';
import TextInput from '@/components/V2/TextInput';
import { AUTH_MODAL_TOAST_CONTAINER } from '@/layouts/ToastWrapper/containerIds';
import { useGetCodeMutation } from '@/store/api/twoFactorAuthentication';

interface EnableSecurityCodeProps {
  onSendCode?: (codeData: { smsSendTo: string; emailSendTo: string; expiry: string }) => void;
  onSkip?: () => void;
  onBack?: () => void;
  isSkipLoading?: boolean;
  isCustomerLoading?: boolean;
}

const EnableSecurityCode: React.FC<EnableSecurityCodeProps> = ({
  onSendCode = () => {},
  onSkip = () => {},
  onBack = () => {},
  isSkipLoading = false,
  isCustomerLoading = false,
}) => {
  const { customer } = useAuthContext();
  const { getPdfById } = usePdfContext();
  const [getCode, { isLoading: isGettingCode }] = useGetCodeMutation();
  const LOADING_TEXT = 'Loading...';
  const privacyPolicyPdf = getPdfById(1); // Privacy Policy (ID: 1)
  // Check if we have customer data and what contact methods are available
  const hasEmail = !!(customer?.email && customer.email.trim());
  const hasPhone = !!(customer?.mobilePhone && customer.mobilePhone.trim());
  const isDataReady = hasEmail || hasPhone; // Need at least one contact method
  const isLoading = !isDataReady;

  const handleSendCode = async () => {
    if (!customer?.customerID) {
      toast(
        ({ closeToast }) => (
          <AuthToast type="error" message="Customer information not available" closeToast={closeToast} />
        ),
        { containerId: AUTH_MODAL_TOAST_CONTAINER }
      );
      return;
    }

    try {
      const response = await getCode({ customerId: customer.customerID }).unwrap();

      if (response.success && response.data) {
        onSendCode(response.data);
      } else {
        toast(
          ({ closeToast }) => (
            <AuthToast type="error" message={response.message || 'Failed to send code'} closeToast={closeToast} />
          ),
          { containerId: AUTH_MODAL_TOAST_CONTAINER }
        );
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.data?.exceptionMessage || 'Failed to send code';
      toast(({ closeToast }) => <AuthToast type="error" message={errorMessage} closeToast={closeToast} />, {
        containerId: AUTH_MODAL_TOAST_CONTAINER,
      });
    }
  };

  return (
    <div>
      <h2 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-lg md:v2-text-4xl v2-text-[#101828] v2-mb-2 md:v2-mb-8">
        Enable Two-Factor Authentication
      </h2>
      {/* <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-lg v2-text-[#101828] v2-mb-4 md:v2-mb-8">
        Select a verification method
      </p> */}
      {/* <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-lg v2-text-[#344054] v2-mb-4 md:v2-mb-8">
        Please share your phone and email to receive a verification code.
      </p> */}
      <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-lg v2-text-[#344054] v2-mb-4 md:v2-mb-8">
        {isLoading ? (
          <>
            <span className="v2-inline-flex v2-items-center v2-gap-2">{LOADING_TEXT}</span>
          </>
        ) : hasPhone && hasEmail ? (
          'By providing your email and phone number, you agree to receive Email/SMS notifications about two-factor authentication from pawTree. Message frequency may vary. Message & data rates may apply.'
        ) : hasEmail ? (
          'By providing your email, you agree to receive Email notifications about two-factor authentication from pawTree.'
        ) : hasPhone ? (
          'By providing your phone number, you agree to receive SMS notifications about two-factor authentication from pawTree. Message frequency may vary. Message & data rates may apply.'
        ) : (
          'By providing your registered contact method, you agree to receive notifications about two-factor authentication from pawTree. Message frequency may vary. Message & data rates may apply.'
        )}
      </p>
      {hasPhone && (
        <div className="v2-flex v2-items-center v2-gap-2 v2-mb-3">
          <label
            htmlFor=""
            className="v2-mb-[0px] v2-text-[#344054] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-max-w-[100px] v2-w-full"
          >
            Your Phone:
          </label>
          <TextInput
            type={'text'}
            placeholder="Your Phone"
            value={isLoading ? LOADING_TEXT : customer?.mobilePhone || customer?.primaryPhone || ''}
            disabled
            inputClassName="v2-font-sweet-sans-pro v2-font-normal v2-text-[#344054] v2-text-lg v2-border-[#D0D5DD] v2-bg-white v2-py-1 v2-px-[10px] v2-cursor-not-allowed !v2-text-[18px] v2-text-center v2-h-[36px] disabled:!v2-bg-white disabled:v2-text-[#344054]"
          ></TextInput>
        </div>
      )}
      {hasEmail && (
        <div className="v2-flex v2-items-center v2-gap-2 v2-mb-6 md:v2-mb-8">
          <label
            htmlFor=""
            className="v2-mb-[0px] v2-text-[#344054] v2-text-lg v2-font-sweet-sans-pro v2-font-normal v2-max-w-[100px] v2-w-full"
          >
            Your Email:
          </label>
          <TextInput
            type={'email'}
            placeholder="Your Email"
            value={isLoading ? LOADING_TEXT : customer?.email || ''}
            disabled
            inputClassName="v2-font-sweet-sans-pro v2-font-normal v2-text-[#344054] v2-text-lg v2-border-[#D0D5DD] v2-bg-white v2-py-1 v2-px-[10px] v2-cursor-not-allowed !v2-text-[18px] v2-text-center v2-h-[36px] disabled:!v2-bg-white disabled:v2-text-[#344054]"
          ></TextInput>
        </div>
      )}
      <Button
        btnSize="md"
        scheme="primary"
        variant="filled"
        type="button"
        className="v2-font-sweet-sans-pro-medium v2-font-medium v2-w-full v2-mb-4"
        onClick={handleSendCode}
        disabled={isGettingCode || isLoading}
        autoFocus
      >
        {isGettingCode ? 'Sending...' : 'Send Code'}
      </Button>
      <Button
        btnSize="md"
        scheme="text"
        variant="outlined"
        type="button"
        className="v2-font-sweet-sans-pro-medium v2-font-medium v2-w-full v2-text-lg !v2-p-0 !v2-text-[#123E2E] hover:!v2-bg-transparent focus:!v2-outline-none v2-mb-4"
        onClick={onSkip}
        disabled={isSkipLoading}
      >
        {isSkipLoading ? LOADING_TEXT : 'Skip'}
      </Button>
      {/* <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-xs md:v2-text-xs v2-text-[#344054] v2-mb-4">
        {isLoading ? (
          <>
            <span className="v2-inline-flex v2-items-center v2-gap-2">{LOADING_TEXT}</span>
          </>
        ) : hasPhone && hasEmail ? (
          'By providing your email and phone number, you agree to receive Email/SMS notifications about two-factor authentication from pawTree. Message frequency may vary. Message & data rates may apply.'
        ) : hasEmail ? (
          'By providing your email, you agree to receive Email notifications about two-factor authentication from pawTree.'
        ) : hasPhone ? (
          'By providing your phone number, you agree to receive SMS notifications about two-factor authentication from pawTree. Message frequency may vary. Message & data rates may apply.'
        ) : (
          'By providing your registered contact method, you agree to receive notifications about two-factor authentication from pawTree. Message frequency may vary. Message & data rates may apply.'
        )}
      </p> */}
      {hasPhone && (
        <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-xs md:v2-text-xs v2-text-[#344054] v2-mb-4">
          You can reply STOP to opt out at any time.{' '}
          {privacyPolicyPdf?.pdfLink ? (
            <a
              href={privacyPolicyPdf.pdfLink}
              target="_blank"
              rel="noopener noreferrer"
              className="v2-text-[#123E2E] v2-underline hover:v2-opacity-70"
            >
              Privacy Policy
            </a>
          ) : (
            'Privacy Policy'
          )}
          . No mobile information will be shared, rented, or sold to third parties for marketing purposes.
        </p>
      )}
      <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-xs md:v2-text-xs v2-text-[#344054] v2-mb-4">
        By sharing your information you agree to receive marketing email from pawTree. You can unsubscribe anytime. For
        more details, see our{' '}
        {privacyPolicyPdf?.pdfLink ? (
          <a
            href={privacyPolicyPdf.pdfLink}
            target="_blank"
            rel="noopener noreferrer"
            className="v2-text-[#123E2E] v2-underline hover:v2-opacity-70"
          >
            Privacy Policy
          </a>
        ) : (
          'Privacy Policy'
        )}
      </p>
    </div>
  );
};

export default EnableSecurityCode;
